/* One shared label for the failure drill-down's agent dimension.
 *
 * All aggregation over failures is server-side (`useErrorBreakdown` — the
 * panel's breakdowns cover the whole window, not the 25-row page it shows), so
 * the only thing the client owns here is the name the untagged bucket goes by.
 *
 * A gateway-vs-provider origin split used to live here, keyed on an
 * "[ai21-gateway]" substring in `row.error`. No service emits that marker
 * (verified absent across the extproc, edge, agentgateway and BFF repos) — it
 * was a mock-fixture convention, so against real data every failure classified
 * as "provider" and the health badge was permanently green. Removed with
 * OPSS-352; the real split is `provider` + `error_type` off the wire. */

import type { ErrorRow } from "./types";

/** Display id for failures the data plane never attributed to an agent. The
 *  BFF sends this same literal on /errors/breakdown's by_agent rows, so the
 *  webapp's label and the server's agree. */
export const UNTAGGED_AGENT = "(untagged)";

/** Rendered when the response cannot attribute a call to an agent either way —
 *  distinct from `(untagged)`, which is a positive statement that the gateway
 *  saw no `x-agent-id`. */
export const UNKNOWN_AGENT = "—";

/** An `agent_id` as a label.
 *
 *  The BFF resolves a genuinely-unattributed call to the `(untagged)` literal in
 *  SQL, so on the view path this is a pass-through and the table row and its
 *  bucket in the breakdown above always read the same. A blank means something
 *  else entirely: only the degraded raw-OTLP path emits it, and only because
 *  that source has no `agent_id` column at all.
 *
 *  Those two must not collapse. They did until OPSS-352 verification against
 *  real staging data, where the BFF predates the repoint and blanks EVERY row:
 *  the panel then labelled all of them `(untagged)`, i.e. claimed as fact that
 *  none of the workspace's traffic was tagged, on data that said nothing of the
 *  kind. Unknown must read as unknown. */
export function agentKey(row: ErrorRow): string {
  return row.agent_id || UNKNOWN_AGENT;
}
